import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { getVariantFitment } from '@theme/memeem-fitment-catalog';

const PAGE_SIZE = 12;
const normalise = (text) => String(text).toLocaleLowerCase().normalize('NFKD').replace(/\p{Diacritic}/gu, '');
const numericID = (id) => String(id ?? '').split('/').pop();
const labelsOnly = (labels) => [...new Set((Array.isArray(labels) ? labels : []).filter((label) => typeof label === 'string' && label.trim()))];

class MemeemProductSheet extends Component {
  #controller;
  #request;
  #selectionGeneration = 0;
  #fitmentGeneration = 0;
  #layout;
  #resizeObserver;
  #messages = {};
  #labels = [];
  #legacyLabels = [];
  #query = '';
  #limit = PAGE_SIZE;

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#layout = this.getBoundingClientRect().width >= 990;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = new ResizeObserver(([entry]) => {
      if (entry) this.#layoutChanged(entry.contentRect.width >= 990);
    });
    this.#resizeObserver.observe(this);
    document.addEventListener(StandardEvents.productSelect, this.#selected, { signal });
    document.addEventListener('click', this.#followAnchor, { signal });
    window.addEventListener('hashchange', this.#hashChanged, { signal });
    this.addEventListener('input', this.#filter, { signal });
    this.addEventListener('click', this.#more, { signal });
    this.#initialise();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#selectionGeneration++;
    this.#request?.abort();
    this.#initialise();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#selectionGeneration++;
    this.#fitmentGeneration++;
    this.#controller?.abort();
    this.#request?.abort();
    this.#resizeObserver?.disconnect();
  }

  #readJSON(selector, fallback) {
    try { return JSON.parse(this.querySelector(selector)?.textContent || 'null') ?? fallback; }
    catch { return fallback; }
  }

  #initialise(openPanels, query = '') {
    this.#messages = this.#readJSON('[data-sheet-messages]', {});
    this.#legacyLabels = labelsOnly(this.#readJSON('[data-fitment-legacy]', []));
    this.#labels = this.#legacyLabels;
    this.#query = query;
    this.#limit = PAGE_SIZE;
    const input = this.querySelector('[data-fitment-search]');
    if (input instanceof HTMLInputElement) input.value = query;
    for (const panel of this.querySelectorAll('[data-sheet-panel]')) {
      panel.open = this.#layout || Boolean(openPanels?.has(panel.dataset.sheetPanel));
    }
    this.#renderFitments();
    this.#loadFitment();
    queueMicrotask(() => { if (this.isConnected) this.#hashChanged(); });
  }

  #layoutChanged = (desktop) => {
    if (desktop === this.#layout) return;
    this.#layout = desktop;
    for (const panel of this.querySelectorAll('[data-sheet-panel]')) panel.open = desktop;
    this.#hashChanged();
  };

  #fitmentStatus(text) {
    const status = this.querySelector('[data-fitment-status]');
    if (status) status.textContent = text || '';
  }

  #clearFitment() {
    this.#fitmentGeneration++;
    this.#labels = [];
    this.#renderFitments();
    this.#fitmentStatus(this.#messages.error);
    this.querySelector('[data-fitment-panel]')?.removeAttribute('aria-busy');
    const universal = this.querySelector('[data-fitment-universal]');
    if (universal) universal.hidden = true;
  }

  async #loadFitment() {
    const panel = this.querySelector('[data-fitment-panel]');
    if (!panel) return;
    const generation = ++this.#fitmentGeneration;
    const sku = this.dataset.sku || '';
    const current = () => this.isConnected && !this.#controller.signal.aborted && generation === this.#fitmentGeneration && sku === this.dataset.sku;
    panel.setAttribute('aria-busy', 'true');
    this.#fitmentStatus(this.#messages.loading);
    const universal = this.querySelector('[data-fitment-universal]');
    if (universal) universal.hidden = true;
    try {
      const result = await getVariantFitment(sku, this.dataset.manifest);
      if (!current()) return;
      // Never attach another supplier SKU's applications to this selection.
      const verified = result.sourceSku === sku;
      const labels = verified ? labelsOnly(result.labels) : [];
      this.#labels = labels.length ? labels : this.#legacyLabels;
      const isUniversal = verified && result.universal === true;
      if (universal) universal.hidden = !isUniversal;
      this.#fitmentStatus(this.#labels.length || isUniversal ? '' : this.#messages.missing);
      this.#renderFitments();
    } catch {
      if (!current()) return;
      this.#labels = this.#legacyLabels;
      this.#fitmentStatus(this.#messages.error);
      this.#renderFitments();
    } finally {
      if (current()) panel.removeAttribute('aria-busy');
    }
  }

  #renderFitments() {
    const list = this.querySelector('[data-fitment-list]');
    if (!list) return;
    const query = normalise(this.#query.trim());
    const matching = query ? this.#labels.filter((label) => normalise(label).includes(query)) : this.#labels;
    const shown = matching.slice(0, this.#limit);
    const fragment = document.createDocumentFragment();
    for (const label of shown) {
      const row = document.createElement('li');
      row.textContent = label;
      fragment.append(row);
    }
    list.replaceChildren(fragment);
    const controls = this.querySelector('[data-fitment-controls]');
    if (controls) controls.hidden = this.#labels.length === 0;
    const count = this.querySelector('[data-fitment-count]');
    if (count) {
      const template = query ? this.#messages.filtered : this.#messages.count;
      count.textContent = this.#labels.length ? String(template || '').replaceAll('__SHOWN__', String(shown.length)).replaceAll('__COUNT__', String(matching.length)).replaceAll('__TOTAL__', String(this.#labels.length)) : '';
    }
    const empty = this.querySelector('[data-fitment-empty]');
    if (empty) empty.hidden = !query || matching.length > 0;
    const more = this.querySelector('[data-fitment-more]');
    if (more) more.hidden = shown.length >= matching.length;
  }

  #filter = (event) => {
    if (!(event.target instanceof HTMLInputElement) || !event.target.matches('[data-fitment-search]')) return;
    this.#query = event.target.value;
    this.#limit = PAGE_SIZE;
    this.#renderFitments();
  };

  #more = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('[data-fitment-more]')) return;
    this.#limit += PAGE_SIZE;
    this.#renderFitments();
  };

  #anchorPanel(hash) {
    let id;
    try { id = decodeURIComponent(hash.slice(1)); } catch { return null; }
    if (!id) return null;
    let target = document.getElementById(id);
    if (!target || !this.contains(target)) {
      const alias = id.startsWith('documents-') ? 'documents' : id.startsWith('compatibility-') ? 'compatibility' : null;
      target = alias ? this.querySelector(`[data-sheet-panel="${alias}"]`) : null;
    }
    if (!target || !this.contains(target)) return null;
    return target.closest('[data-sheet-panel]');
  }

  #openAnchor(hash, scroll = true) {
    const panel = this.#anchorPanel(hash);
    if (!panel) return false;
    panel.open = true;
    if (scroll) requestAnimationFrame(() => { if (this.isConnected && panel.isConnected) panel.scrollIntoView({ block: 'start' }); });
    return true;
  }

  #hashChanged = () => this.#openAnchor(location.hash);

  #followAnchor = (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || !(event.target instanceof Element)) return;
    const anchor = event.target.closest('a[href]');
    if (!anchor || anchor.target === '_blank') return;
    const url = new URL(anchor.href, location.href);
    if (!url.hash || url.origin !== location.origin || url.pathname !== location.pathname || url.search !== location.search || !this.#anchorPanel(url.hash)) return;
    event.preventDefault();
    if (url.hash !== location.hash) history.pushState(null, '', url.hash);
    this.#openAnchor(url.hash);
  };

  #selected = async (event) => {
    if (event.target instanceof Element && event.target.closest('product-card, .mm-pcard, quick-add-dialog, quick-add-component')) return;
    const sourceProduct = event.target instanceof Element ? event.target.closest('[data-product-id]')?.dataset.productId : null;
    if (sourceProduct && numericID(sourceProduct) !== this.dataset.productId) return;
    // Invalidate both in-flight work and unresolved older product-select promises now.
    const generation = ++this.#selectionGeneration;
    this.#fitmentGeneration++;
    this.#request?.abort();
    const current = () => this.isConnected && !this.#controller.signal.aborted && generation === this.#selectionGeneration;
    let request;
    this.setAttribute('aria-busy', 'true');
    try {
      const { detail } = await event.promise;
      if (!current()) return;
      if (numericID(detail?.productId || this.dataset.productId) !== this.dataset.productId) { this.#loadFitment(); return; }
      if (!detail?.resource?.id) { this.#clearFitment(); return; }
      const id = numericID(detail.resource.id);
      if (id === this.dataset.variantId) { this.#loadFitment(); return; }
      request = this.#request = new AbortController();
      const url = new URL(this.dataset.url, location.origin);
      url.searchParams.set('variant', id);
      url.searchParams.set('section_id', this.dataset.sectionId);
      const response = await fetch(url, { signal: request.signal });
      if (!response.ok) throw new Error('Product information unavailable');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (!current() || request.signal.aborted) return;
      const next = html.querySelector('memeem-product-sheet');
      if (!next || next.dataset.productId !== this.dataset.productId || next.dataset.variantId !== id) throw new Error('Unexpected product identity');
      const openPanels = new Set([...this.querySelectorAll('[data-sheet-panel][open]')].map((panel) => panel.dataset.sheetPanel));
      const query = this.#query;
      this.dataset.variantId = id;
      this.dataset.sku = next.dataset.sku || '';
      this.dataset.manifest = next.dataset.manifest || '';
      this.replaceChildren(...next.childNodes);
      super.updatedCallback();
      this.#initialise(openPanels, query);
    } catch (error) {
      if (!current() || error?.name === 'AbortError') return;
      this.#clearFitment();
      const status = this.querySelector('[data-sheet-status]');
      if (status) status.hidden = false;
    } finally {
      if (current()) this.removeAttribute('aria-busy');
    }
  };
}

if (!customElements.get('memeem-product-sheet')) customElements.define('memeem-product-sheet', MemeemProductSheet);
